// Call review (D-112, QA-01 to QA-05): the owner's QA team checks the AI's reading of each call. A tester gets every
// call they have not reviewed, oldest first; a senior the calls a tester flagged, with the testers' answers. On the
// left the call, its recording, the transcript, the AI's reading and what the AI did; on the right the checks (Yes or
// No), a note and Save and next. Keyboard: Y and N answer the highlighted check, up and down move, space plays,
// left and right skip five seconds, Enter saves and opens the next call.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { fmtDateTime, fmtDuration, taskSourceLabel, taskTypeLabel } from "../lib/format";
import {
  allAnswered,
  answered,
  type Answers,
  isFlagged,
  maskedNumber,
  nextUnanswered,
  stageLabel,
  stagesFor,
} from "../lib/review";
import type { QaCall, QaLayer, QaMe, QaNext, QaStage, QaSummary, TaskType } from "../lib/types";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, InterestBadge, Loading, Page, useAction } from "../app/ui";

const LAYERS: [QaLayer, string][] = [
  ["transcript", "Transcript"],
  ["analysis", "Analysis"],
  ["both", "Both"],
  ["none", "Neither"],
];

function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.isContentEditable);
}

export function Review() {
  const { api, tz } = useMember();
  const [params, setParams] = useSearchParams();
  const { busy, run } = useAction();
  const [me, setMe] = useState<QaMe | null>(null);
  const [stage, setStage] = useState<QaStage | null>(null);
  const [progress, setProgress] = useState<QaNext | null>(null);
  const [data, setData] = useState<QaCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Answers>({});
  const [active, setActive] = useState(0);
  const [note, setNote] = useState("");
  const [flag, setFlag] = useState(false);
  const [layer, setLayer] = useState<QaLayer | null>(null);
  const [adding, setAdding] = useState("");
  const [summary, setSummary] = useState<QaSummary | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  // The call just saved, to go back to and change an answer.
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  // The recording: a signed link made when play is pressed (each one is audit-logged).
  const audio = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [pendingT, setPendingT] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [rate, setRate] = useState(1);
  const [playError, setPlayError] = useState<string | null>(null);
  const [autoplay, setAutoplay] = useState(() => {
    try {
      return localStorage.getItem("review.autoplay") === "1";
    } catch {
      return false;
    }
  });

  const open = useCallback(async (recordingId: string | null, keep?: QaNext) => {
    setSrc(null);
    setNow(0);
    setPlayError(null);
    setHint(null);
    if (keep) setProgress(keep);
    if (!recordingId) {
      setData(null);
      return;
    }
    const call = await api.qaCall(recordingId);
    const mine = call.reviews.find((r) => r.mine && r.stage === (stage ?? "tester"));
    setData(call);
    setAnswers(mine?.answers ?? {});
    setNote(mine?.note ?? "");
    setFlag(false);
    setLayer(mine?.layer ?? null);
    setActive(0);
  }, [api, stage]);

  // Who this is, and their first stage.
  useEffect(() => {
    void (async () => {
      try {
        const who = await api.qaMe();
        setMe(who);
        setStage(stagesFor(who)[0] ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  // The next call at this stage, or the one asked for in the address.
  useEffect(() => {
    if (!stage) return;
    void (async () => {
      setLoading(true);
      try {
        const next = await api.qaNext(stage);
        setProgress(next);
        await open(params.get("call") ?? next.recording_id);
        if (me?.can_manage) setSummary(await api.qaSummary());
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [api, stage]);

  const play = async (t = 0) => {
    if (!data) return;
    setPlayError(null);
    const el = audio.current;
    if (src && el) {
      el.currentTime = t;
      void el.play().catch(() => {});
      return;
    }
    try {
      const res = await api.playback(data.call.recording_id, t);
      setPendingT(t);
      setSrc(res.url);
    } catch (e) {
      setPlayError(e instanceof Error ? e.message : "The recording could not be played.");
    }
  };

  useEffect(() => {
    if (autoplay && data && !data.call.audio_deleted) void play(0);
  }, [data?.call.recording_id]);

  const checks = data?.checks ?? [];
  const flagged = isFlagged(answers, flag);

  const answer = (i: number, value: boolean) => {
    const key = checks[i]?.key;
    if (!key) return;
    const next = { ...answers, [key]: value };
    setAnswers(next);
    const j = nextUnanswered(checks, next, i);
    if (j >= 0) setActive(j);
  };

  // A senior's verdict can run the call again with the note, so the correction reaches the AI (D-116).
  const canRerun = stage === "senior" && !!layer && layer !== "none" && note.trim() !== "";
  const save = async (rerun = false) => {
    if (!data || !stage) return;
    if (!allAnswered(checks, answers)) {
      const j = nextUnanswered(checks, answers, -1);
      if (j >= 0) setActive(j);
      setHint("Answer every check, Yes or No.");
      return;
    }
    if (stage === "senior" && flagged && !layer) {
      setHint("Say where the error was: the transcript, the analysis, both or neither.");
      return;
    }
    const done = await run(async () => {
      const r = await api.qaSave(data.call.recording_id, stage, {
        answers: answered(answers),
        note: note.trim() || undefined,
        flag,
        layer: stage === "senior" ? layer : null,
      });
      if (rerun && canRerun) {
        await api.rerunRecording(data.call.recording_id, { layer, text: note.trim() });
      }
      if (params.get("call")) setParams({}, { replace: true });
      setLastSaved(data.call.recording_id);
      await open(r.next.recording_id, r.next);
      if (me?.can_manage) setSummary(await api.qaSummary());
    }, rerun ? "Saved, and the call is read again with your note" : "Saved");
    if (done) window.scrollTo({ top: 0 });
  };

  const addCheck = async () => {
    const label = adding.trim();
    if (!label || !data) return;
    if (
      await run(async () => {
        const check = await api.qaAddCheck(label);
        setData({ ...data, checks: [...data.checks, check] });
        setAdding("");
      }, "Check added for everyone")
    ) setActive(checks.length);
  };

  // Keyboard: answer, move, play and save without the mouse.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!data) return;
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void save();
        return;
      }
      if (typing(e.target) || e.altKey || e.metaKey || e.ctrlKey) return;
      const el = audio.current;
      switch (e.key) {
        case "y":
        case "Y":
          answer(active, true);
          break;
        case "n":
        case "N":
          answer(active, false);
          break;
        case "ArrowDown":
        case "j":
          e.preventDefault();
          setActive((a) => Math.min(a + 1, checks.length - 1));
          break;
        case "ArrowUp":
        case "k":
          e.preventDefault();
          setActive((a) => Math.max(a - 1, 0));
          break;
        case " ":
          e.preventDefault();
          if (el && src) {
            if (el.paused) void el.play().catch(() => {});
            else el.pause();
          } else void play(0);
          break;
        case "ArrowLeft":
          if (el) el.currentTime = Math.max(0, el.currentTime - 5);
          break;
        case "ArrowRight":
          if (el) el.currentTime = el.currentTime + 5;
          break;
        case "f":
        case "F":
          setFlag((x) => !x);
          break;
        case "Enter":
          e.preventDefault();
          void save();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (loading && !data && !me) return <Loading />;
  if (error && !me) return <ErrorNote error={error} />;
  if (me && !me.can_review) {
    return (
      <Page title="Review calls">
        <Empty>Call review is for reviewers. An owner or admin can make you one on the Team page.</Empty>
      </Page>
    );
  }

  const stages = me ? stagesFor(me) : [];
  const segments = data?.transcript?.segments ?? [];
  const current = segments.findIndex((s, i) =>
    s.start != null && now >= s.start && (segments[i + 1]?.start == null || now < (segments[i + 1].start as number))
  );
  const output = data?.analysis?.output ?? {};
  const testers = (data?.reviews ?? []).filter((r) => r.stage === "tester" && !(r.mine && stage === "tester"));
  const insights = data?.insights ?? [];

  return (
    <Page
      title="Review calls"
      sub={progress
        ? (
          <span className="row">
            <strong>{progress.left}</strong> left · {progress.done} of {progress.total} reviewed
            {stages.length > 1 && (
              <span className="row" style={{ marginLeft: 12 }}>
                {stages.map((s) => (
                  <button
                    key={s}
                    className={s === stage ? "small primary" : "small"}
                    onClick={() => setStage(s)}
                  >
                    {stageLabel[s]}
                  </button>
                ))}
              </span>
            )}
          </span>
        )
        : null}
    >
      <ErrorNote error={error} />
      {summary && <ReviewSummary summary={summary} />}
      {!data && !loading && (
        <Empty>
          {stage === "senior"
            ? "No flagged calls waiting. Calls a tester flags appear here."
            : "All caught up: you have reviewed every call. New calls appear here once they are processed."}
        </Empty>
      )}
      {data && (
        <div className="review-grid">
          <div className="stack">
            <Card
              title={`${data.call.customer ?? "Customer"} · ${maskedNumber(data.call.number_end)}`}
              actions={
                <span className="row">
                  {[1, 1.25, 1.5, 2].map((r) => (
                    <button
                      key={r}
                      className={r === rate ? "small primary" : "small"}
                      onClick={() => {
                        setRate(r);
                        if (audio.current) audio.current.playbackRate = r;
                      }}
                    >
                      {r}×
                    </button>
                  ))}
                  <button className="primary small" disabled={data.call.audio_deleted} onClick={() => void play(0)}>
                    ▶ Play
                  </button>
                </span>
              }
            >
              <div className="faint">
                {fmtDateTime(data.call.started_at, tz)} · {data.call.direction ?? "call"} ·{" "}
                {fmtDuration(data.call.duration_s)} · {data.call.caller ?? ""}
                {data.call.lead_stage ? ` · ${data.call.lead_stage.replaceAll("_", " ")}` : ""}
              </div>
              <ErrorNote error={playError} />
              {data.call.audio_deleted && <p className="faint">The audio was deleted under the retention policy.</p>}
              {src && (
                <audio
                  ref={audio}
                  controls
                  src={src}
                  style={{ width: "100%", marginTop: 8 }}
                  onLoadedMetadata={(e) => {
                    e.currentTarget.playbackRate = rate;
                    if (pendingT != null) e.currentTarget.currentTime = pendingT;
                    setPendingT(null);
                    void e.currentTarget.play().catch(() => {});
                  }}
                  onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
                  onError={() => setSrc(null)}
                />
              )}
              <label className="row faint" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={autoplay}
                  onChange={(e) => {
                    setAutoplay(e.target.checked);
                    try {
                      localStorage.setItem("review.autoplay", e.target.checked ? "1" : "0");
                    } catch {
                      // Private window: the choice lasts this visit.
                    }
                  }}
                />
                Play each call when it opens
              </label>
            </Card>

            <Card title={`Transcript${data.transcript ? ` · ${data.transcript.model}` : ""}`}>
              {segments.length === 0 && <Empty>{data.transcript?.text || "Nothing was said."}</Empty>}
              <div className="transcript">
                {segments.map((s, i) => (
                  <button
                    key={i}
                    className={`seg${i === current ? " now" : ""}`}
                    onClick={() => void play(s.start ?? 0)}
                  >
                    <span className="t">{fmtDuration(s.start)}</span>
                    <span className={`who${/customer/i.test(s.speaker ?? "") ? " customer" : ""}`}>
                      {s.speaker ?? "Speaker"}
                    </span>
                    <span className="text">{s.text}</span>
                  </button>
                ))}
              </div>
            </Card>

            <Card title={`What the AI heard${data.analysis ? ` · ${data.analysis.prompt_version}` : ""}`}>
              {!data.analysis && <Empty>Not analysed.</Empty>}
              {data.analysis && (
                <>
                  <p>{output.summary ?? data.analysis.summary}</p>
                  <div className="row">
                    <strong>Interest:</strong>
                    {output.interest && <InterestBadge interest={output.interest.level} />}
                    {output.call_outcome && <span className="faint">{output.call_outcome.replaceAll("_", " ")}</span>}
                  </div>
                  {output.interest?.evidence?.map((ev, i) => <div key={i} className="quote">“{ev.quote}”</div>)}
                  {output.next_action && (
                    <div style={{ marginTop: 10 }}>
                      <strong>Next:</strong>{" "}
                      {taskTypeLabel[output.next_action.type as TaskType] ?? output.next_action.type} ·{" "}
                      {output.next_action.reason}
                      {output.next_action.time_hint?.text && (
                        <div className="faint">When: {output.next_action.time_hint.text}</div>
                      )}
                      {output.next_action.customer_said && (
                        <div className="quote">“{output.next_action.customer_said}”</div>
                      )}
                    </div>
                  )}
                  {insights.filter((i) => i.kind === "objection").length > 0 && (
                    <h3 style={{ margin: "12px 0 4px" }}>Objections</h3>
                  )}
                  {insights.filter((i) => i.kind === "objection").map((o, i) => (
                    <div className="list-item" key={i}>
                      <strong>{o.detail || o.category}</strong>
                      {o.quote && <div className="quote">“{o.quote}”</div>}
                    </div>
                  ))}
                  {insights.filter((i) => i.kind !== "objection").map((o, i) => (
                    <div className="list-item faint" key={`x${i}`}>{o.kind}: {o.detail}</div>
                  ))}
                  {(output.checklist ?? []).length > 0 && <h3 style={{ margin: "12px 0 4px" }}>Call checklist</h3>}
                  {(output.checklist ?? []).map((c) => (
                    <div className="row" key={c.key}>
                      <span className={`badge ${c.result === "yes" ? "ok" : c.result === "no" ? "danger" : ""}`}>
                        {c.result === "yes" ? "Done" : c.result === "no" ? "Missed" : "Didn't apply"}
                      </span>
                      <span>{c.label}</span>
                    </div>
                  ))}
                  {output.facts && Object.keys(output.facts).length > 0 && (
                    <p className="faint">
                      {Object.entries(output.facts).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </p>
                  )}
                </>
              )}
            </Card>

            <Card title="What the AI did">
              {data.outcome.follow_up
                ? (
                  <p>
                    <strong>
                      {taskTypeLabel[data.outcome.follow_up.type as TaskType] ?? data.outcome.follow_up.type}
                    </strong>{" "}
                    {data.outcome.follow_up.due_has_time
                      ? fmtDateTime(data.outcome.follow_up.due_at, tz)
                      : `${fmtDateTime(data.outcome.follow_up.due_at, tz).split(",")[0]}, any time`}
                    <span className="faint">
                      {" "}· {taskSourceLabel(data.outcome.follow_up.source, null)} · {data.outcome.follow_up.status}
                    </span>
                    <br />
                    <span className="faint">{data.outcome.follow_up.reason}</span>
                  </p>
                )
                : <p className="faint">No follow-up from this reading.</p>}
              {data.outcome.ai_outcome && <p className="faint">AI's outcome: {data.outcome.ai_outcome}</p>}
              {data.outcome.her_choice && (
                <p className="faint">Telecaller chose: {data.outcome.her_choice.replaceAll("_", " ")}</p>
              )}
            </Card>
          </div>

          <div className="review-side">
            <Card title={stage === "senior" ? "Your verdict" : "Your checks"}>
              {stage === "senior" && testers.length > 0 && (
                <div className="stack" style={{ marginBottom: 12 }}>
                  {testers.map((t) => (
                    <div key={t.reviewer + t.updated_at} className="list-item">
                      <strong>{t.reviewer}</strong>
                      {data.checks.map((c) =>
                        typeof t.answers[c.key] === "boolean" && (
                          <div key={c.key} className="row faint">
                            <span className={`badge ${t.answers[c.key] ? "ok" : "danger"}`}>
                              {t.answers[c.key] ? "Yes" : "No"}
                            </span>
                            <span>{c.label}</span>
                          </div>
                        )
                      )}
                      {t.note && <div className="quote">{t.note}</div>}
                    </div>
                  ))}
                </div>
              )}
              {checks.map((c, i) => (
                <div
                  key={c.key}
                  className={`review-check${i === active ? " active" : ""}`}
                  onClick={() => setActive(i)}
                >
                  <span>{c.label}</span>
                  <span className="row">
                    <button
                      className={answers[c.key] === true ? "small primary" : "small"}
                      aria-pressed={answers[c.key] === true}
                      onClick={() => answer(i, true)}
                    >
                      Yes
                    </button>
                    <button
                      className={answers[c.key] === false ? "small danger solid" : "small"}
                      aria-pressed={answers[c.key] === false}
                      onClick={() => answer(i, false)}
                    >
                      No
                    </button>
                  </span>
                </div>
              ))}
              <div className="row" style={{ marginTop: 8 }}>
                <input
                  value={adding}
                  onChange={(e) => setAdding(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void addCheck();
                    }
                  }}
                  placeholder="Add a check for everyone"
                  maxLength={160}
                />
                <button className="small" disabled={busy || adding.trim().length < 2} onClick={() => void addCheck()}>
                  Add
                </button>
              </div>
              <label className="field" style={{ marginTop: 12 }}>
                <span>Note</span>
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What was wrong, in a line or two"
                />
              </label>
              {stage === "senior" && (
                <div className="field">
                  <span>Where was the error?</span>
                  <div className="row">
                    {LAYERS.map(([value, label]) => (
                      <button
                        key={value}
                        className={layer === value ? "small primary" : "small"}
                        aria-pressed={layer === value}
                        onClick={() => setLayer(layer === value ? null : value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {stage === "tester" && (
                <label className="row" style={{ marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={flagged}
                    disabled={flagged && !flag}
                    onChange={() => setFlag(!flag)}
                  />
                  {flagged && !flag ? "Flagged for the seniors (a No flags the call)" : "Flag for the seniors"}
                </label>
              )}
              {hint && <p className="faint" style={{ color: "var(--danger)" }}>{hint}</p>}
              <div className="row" style={{ marginTop: 12 }}>
                <button className="primary" disabled={busy} onClick={() => void save()}>Save and next</button>
                {stage === "senior" && (
                  <button
                    disabled={busy || !canRerun}
                    title={canRerun ? undefined : "Say where the error was and write the note first"}
                    onClick={() => void save(true)}
                  >
                    Save and run AI again
                  </button>
                )}
                {lastSaved && lastSaved !== data.call.recording_id && (
                  <button
                    className="link small"
                    disabled={busy}
                    onClick={() => void open(lastSaved)}
                  >
                    Back to the last call
                  </button>
                )}
                {me?.can_manage && <Link to={`/calls/${data.call.recording_id}`}>Open the call</Link>}
              </div>
              <p className="faint" style={{ marginTop: 8 }}>
                Keys: Y or N answers, ↑ ↓ move, space plays, ← → skip 5 s, F flags, Enter saves and opens the next call.
              </p>
            </Card>
          </div>
        </div>
      )}
    </Page>
  );
}

/** How the review is going, for owners and admins (QA-05). */
function ReviewSummary({ summary }: { summary: QaSummary }) {
  return (
    <Card title="How the review is going">
      <div className="row" style={{ flexWrap: "wrap", gap: 16 }}>
        <span>{summary.reviewable} calls to review</span>
        <span>{summary.flagged_calls} flagged</span>
        {summary.reviewers.map((r) => (
          <span key={r.member_id}>
            <strong>{r.name}</strong>{" "}
            <span className="faint">
              {r.qa_role === "senior" ? `${r.verdicts} verdicts` : `${r.reviewed} reviewed, ${r.flagged} flagged`}
            </span>
          </span>
        ))}
      </div>
      <div className="row faint" style={{ flexWrap: "wrap", gap: 16, marginTop: 6 }}>
        {summary.checks.map((c) => (
          <span key={c.key} title={c.label}>
            {c.label}: {c.yes} yes, {c.no} no
          </span>
        ))}
        {Object.entries(summary.layers).map(([k, v]) => <span key={k}>Errors in {k}: {v}</span>)}
      </div>
    </Card>
  );
}
